import { AddressSearch } from '@/components/maps/AddressSearch';
import { GeocodingResult } from '@/components/maps/mapbox';
import SiteLocationMap from '@/components/maps/SiteLocationMap';
import {
  Form,
  FormField,
  FormLabel,
  FormItem,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressDto, AddressSchema } from '@/types/common-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

// Form schema
const addressSchema = z.object({
  address: AddressSchema,
});

type DeviceMapProps = {
  address?: AddressDto;
  setAddress: (value?: AddressDto) => void;
};
const DevicesMap = ({ address, setAddress }: DeviceMapProps) => {
  const [mapLat, setMapLat] = useState(address?.lat ?? -36.8509);
  const [mapLon, setMapLon] = useState(address?.lon ?? 174.7645);

  const form = useForm<z.infer<typeof addressSchema>>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      address: { ...address, lat: mapLat, lon: mapLon },
    },
  });

  form.watch((values) => setAddress(values.address));

  useEffect(() => {
    const values = form.getValues();
    console.log(Object.keys(values.address));

    if (address)
      Object.keys(address).map((key) => {
        const keyTyped = key as unknown as keyof AddressDto;

        if (address[keyTyped] != values.address[keyTyped]) {
          form.setValue(`address.${keyTyped}`, address[keyTyped]);
        }
      });
  }, [address]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})} className="space-y-2 pt-1 ">
        <div className="grid gap-2">
          <Label>Address & Location</Label>
          <div className="grid grid-cols-2 gap-4">
            {/* Left side - Address inputs and details */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="address.addressLine1"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <AddressSearch
                        onSelect={(result: GeocodingResult) => {
                          const newAddress = {
                            addressLine1: result.address || '',
                            addressLine2: form.getValues('address.addressLine2') || '',
                            city: result.city || '',
                            region: result.state || '',
                            country: result.country || '',
                            postCode: result.postcode || '',
                            lat: result.center[1],
                            lon: result.center[0],
                          };
                          form.setValue('address', newAddress, { shouldValidate: true });
                          setMapLat(result.center[1]);
                          setMapLon(result.center[0]);
                        }}
                        placeholder="Search for an address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address.addressLine2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="Address line 2 (optional)" {...field} value={field.value ?? undefined} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {
                <div className="rounded-lg border border-gray-700 bg-[#111] p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-gray-400">Address Line 1</div>
                      <div className="text-sm text-white">
                        {form.watch('address.addressLine1') || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-400">Address Line 2</div>
                      <div className="text-sm text-white">
                        {form.watch('address.addressLine2') || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-400">City</div>
                      <div className="text-sm text-white">{form.watch('address.city') || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-400">Region</div>
                      <div className="text-sm text-white">
                        {form.watch('address.region') || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-400">Post Code</div>
                      <div className="text-sm text-white">
                        {form.watch('address.postCode') || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-400">Country</div>
                      <div className="text-sm text-white">
                        {form.watch('address.country') || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Right side - Map */}
            <div className="h-[300px]">
              <SiteLocationMap
                coordinates={{ lat: mapLat, lng: mapLon }}
                onCoordinatesChange={(coords) => {
                  setMapLat(coords.lat);
                  setMapLon(coords.lng);
                  const newAddress = {
                    ...form.getValues('address'),
                    lat: coords.lat,
                    lon: coords.lng,
                  };
                  form.setValue('address', newAddress, { shouldValidate: true });
                }}
                onAddressChange={() => {}}
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default DevicesMap;
